import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import {
  UserPlusIcon,
  UserGroupIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

export function HowItWorks() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  const steps = [
    {
      number: '1',
      icon: UserPlusIcon,
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description'),
      color: 'from-primary-500 to-primary-600',
    },
    {
      number: '2',
      icon: UserGroupIcon,
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
      color: 'from-green-500 to-green-600',
    },
    {
      number: '3',
      icon: ShareIcon,
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description'),
      color: 'from-purple-500 to-purple-600',
    },
  ];

  return (
    <section className="py-16 md:py-24">
      <Container>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section header */}
          <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {t('howItWorks.title')}{' '}
              <span className="text-gradient">{t('howItWorks.titleHighlight')}</span>
              {t('howItWorks.titleEnd')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('howItWorks.subtitle')}
            </p>
          </motion.div>

          {/* Steps */}
          <div className="relative">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-24 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-200 via-green-200 to-purple-200 dark:from-primary-800 dark:via-green-800 dark:to-purple-800" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="relative text-center"
                >
                  {/* Step number with icon */}
                  <div className="relative inline-flex flex-col items-center">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg mb-6`}>
                      <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white dark:bg-slate-800 rounded-full border-2 border-gray-200 dark:border-slate-700 flex items-center justify-center shadow">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-slate-400 max-w-xs mx-auto">
                    {step.description}
                  </p>

                  {/* Arrow for mobile */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden flex justify-center my-6">
                      <svg className="w-6 h-6 text-gray-300 dark:text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('howItWorks.cta')}
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
